import { useCallback, useEffect, useMemo, useState } from "react";
import {
    DEFAULT_INVOICE_TEMPLATE_SORT,
    InvoiceTemplateArchivedFilter,
    InvoiceTemplateSort,
} from "@common/types/invoiceTemplate";
import {
    InvoiceTemplateColumn,
    InvoiceTemplateView,
} from "@common/types/views/invoiceTemplateViews";
import { useInvoiceTemplatesForEntities } from "@api/invoiceTemplate.api";
import {
    ExcludeEmptyMockEntitiesMode,
    useEntitiesContexts,
} from "@hooks/useEntitiesContexts";
import { useOverlays } from "contexts/OverlayContext";
import { Drawer } from "@design-system/Drawer/Drawer";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { InvoiceTemplateRow } from "./invoiceTemplates.types";
import { InvoiceTemplatesListFooter } from "./InvoiceTemplatesListFooter";
import {
    defaultInvoiceTemplateView,
    invoiceTemplatesViewConfig,
} from "./invoiceTemplatesViewConfig";
import styles from "./InvoiceTemplatesDrilldownDrawer.module.scss";

const PAGE_SIZE = 25;

const EXCLUDED_COLUMNS = new Set<InvoiceTemplateColumn>([
    InvoiceTemplateColumn.ACTIONS,
    InvoiceTemplateColumn.INVOICES,
]);

const drilldownView: InvoiceTemplateView = {
    ...defaultInvoiceTemplateView,
    columns: defaultInvoiceTemplateView.columns.filter(
        (column) => !EXCLUDED_COLUMNS.has(column.id),
    ),
};

interface Props {
    open: boolean;
    onClose: () => void;
    catalogItemIds: string[];
}

const InvoiceTemplatesDrilldownContent: React.FC<
    Pick<Props, "catalogItemIds">
> = ({ catalogItemIds }) => {
    const { openOverlay } = useOverlays();
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState<InvoiceTemplateSort>(
        DEFAULT_INVOICE_TEMPLATE_SORT,
    );
    const entityContexts = useEntitiesContexts({
        excludeEmptyMockEntities: ExcludeEmptyMockEntitiesMode.NEVER,
    });
    const entityIds = useMemo(
        () => (entityContexts ?? []).map(({ entity }) => entity.id),
        [entityContexts],
    );

    const {
        templates,
        total,
        pageCount,
        isLoading: isTemplatesLoading,
    } = useInvoiceTemplatesForEntities({
        entityIds,
        archivedFilter: InvoiceTemplateArchivedFilter.ACTIVE,
        catalogItemIds,
        sort,
        page,
        limit: PAGE_SIZE,
    });
    const isLoading = entityContexts === undefined || isTemplatesLoading;

    useEffect(() => {
        setPage((prev) => Math.min(prev, pageCount));
    }, [pageCount]);

    const handleRowClick = useCallback(
        (row?: InvoiceTemplateRow) => {
            if (row) {
                openOverlay("nativeInvoiceDrawer", {
                    editTemplate: {
                        templateId: row.id,
                        entityId: row.entityId,
                    },
                });
            }
        },
        [openOverlay],
    );

    const handleSortChange = useCallback((newSort: InvoiceTemplateSort) => {
        setSort(newSort);
        setPage(1);
    }, []);

    const handleSortRemove = useCallback(() => {
        setSort(DEFAULT_INVOICE_TEMPLATE_SORT);
        setPage(1);
    }, []);

    return (
        <div className={styles.content}>
            <ViewTable
                data={isLoading && templates.length === 0 ? null : templates}
                view={drilldownView}
                config={invoiceTemplatesViewConfig}
                sort={sort}
                onSortChange={handleSortChange}
                onSortRemove={handleSortRemove}
                onRowClick={handleRowClick}
                rowBorders={false}
                isParentSelectable={false}
                emptyView={() => <EmptyState header="No templates found." />}
                footer={
                    <InvoiceTemplatesListFooter
                        total={total}
                        currentPage={page}
                        totalPages={pageCount}
                        onPageChange={setPage}
                        isLoading={isLoading}
                    />
                }
            />
        </div>
    );
};

export const InvoiceTemplatesDrilldownDrawer: React.FC<Props> = ({
    open,
    onClose,
    catalogItemIds,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const shouldShowContent = isOpen || open;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            onAnimationEnd={setIsOpen}
            headerAddonLeft="Invoice templates"
            noPadding
        >
            {shouldShowContent && (
                <InvoiceTemplatesDrilldownContent
                    catalogItemIds={catalogItemIds}
                />
            )}
        </Drawer>
    );
};
